import { Info, X } from 'shared/icons';
import { useCallback, useState } from 'react';
import api from '../../../services/api';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import Textarea from '../../ui/Textarea';
import { BuildBlockCode } from '../collector/collectorScan';
import InventoryOccupancyPanel from './InventoryOccupancyPanel';

/**
 * NOVA CONTAGEM (§6.9) — "o Supervisor escolhe os BLOCOS".
 *
 * ⚠️ ESCOLHER É CLICAR NO MAPA, e o mapa é o HEATMAP. Não é enfeite: o critério
 * real de "que bloco eu conto hoje" é o que está cheio e o que está parado —
 * a informação já está na cor. Um formulário com dois campos pediria ao
 * Supervisor que soubesse de cor o que a tela ao lado mostra.
 *
 * ⚠️ E A OS NASCE PELA CRIAÇÃO QUE JÁ EXISTE: `api.createWarehouseServiceOrder`
 * (o `POST /service-orders` do M5), com `count_blocks: [{rack, bay}]` no corpo
 * (`rack` = LETRA do porta-pallet, `bay` = nº do bloco). Não existe rota de
 * "criar contagem" — o contrato do M9 proíbe um segundo caminho de criação de
 * OS, e o servidor é quem expande bloco → posições.
 */

const COUNT_TYPE_CODE = 'COUNT';

// Chave de bloco compartilhada com o WarehouseMap/heatmap: `${rack}:${bay}` ("A:1").
const blockKey = (entry) => `${entry.rack}:${entry.bay}`;

const CountOrderCreateModal = ({ open, label, onClose, onCreated }) => {
    const [selected, setSelected] = useState([]);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const toggleBlock = useCallback((entry) => {
        setSelected((current) => {
            const key = blockKey(entry);
            return current.some((item) => blockKey(item) === key)
                ? current.filter((item) => blockKey(item) !== key)
                : [...current, entry];
        });
    }, []);

    const reset = () => {
        setSelected([]);
        setNotes('');
        setSaveError(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleCreate = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const created = await api.createWarehouseServiceOrder({
                type_code: COUNT_TYPE_CODE,
                // READY: a contagem é montada para ser executada agora — o
                // BACKLOG é a fila de DECISÃO do Supervisor (§6.5), e ele já
                // decidiu ao escolher os blocos.
                status: 'READY',
                count_blocks: selected.map((entry) => ({ rack: entry.rack, bay: entry.bay })),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
            });
            reset();
            onCreated(created);
        } catch (error) {
            setSaveError(error?.message || 'Erro ao criar');
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return null;
    }

    return (
        <Modal isOpen={open} onClose={handleClose} title={label('wms.count.new')} size="4xl">
            <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl border-2 border-info-400 bg-info-soft px-4 py-3 dark:border-info-700 dark:bg-info-900/30">
                    <Info size={22} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                    <div className="space-y-1">
                        <p className="text-sm font-semibold text-info-text dark:text-info-200">
                            {label('wms.count.pick_blocks')}
                        </p>
                        <p className="text-sm text-info-text dark:text-info-200">
                            {label('wms.count.blocked_excluded')}
                        </p>
                    </div>
                </div>

                {selected.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-mono-600 dark:text-mono-400">
                            {label('wms.count.picked_blocks')}:
                        </span>
                        {selected.map((entry) => (
                            <button
                                type="button"
                                key={blockKey(entry)}
                                onClick={() => toggleBlock(entry)}
                                className="inline-flex items-center gap-1 rounded-full border border-mono-300 px-3 py-1 font-mono text-xs hover:border-danger-400 dark:border-mono-700"
                            >
                                {/* O chip fala o código da etiqueta física do bloco (`A-01`). */}
                                {BuildBlockCode(entry.rack, entry.bay)}
                                <X size={12} />
                            </button>
                        ))}
                    </div>
                ) : null}

                <InventoryOccupancyPanel
                    label={label}
                    selectedBlocks={selected.map(blockKey)}
                    onBlockToggle={toggleBlock}
                />

                <Textarea
                    label={label('wms.field.notes')}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={2}
                />

                {saveError ? (
                    <p className="rounded-lg border border-danger-300 bg-danger-soft px-3 py-2 text-sm text-danger-text dark:border-danger-800 dark:bg-danger-900/25 dark:text-danger-200">
                        {saveError}
                    </p>
                ) : null}

                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={handleClose} disabled={saving}>
                        {label('wms.common.cancel')}
                    </Button>
                    <Button onClick={handleCreate} disabled={saving || selected.length === 0}>
                        {label('wms.count.new')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CountOrderCreateModal;
